import { crossingSpanFor, neighboursOf, type MapSide, type Neighbour } from "../document/layout.ts";
import type { MapDocument, Seam, SeamCrossing } from "../document/types.ts";
import type { SeamGround, ValidationIssue } from "./adapter.ts";
import { OPPOSITE, alongOrigin, edgeBox, painted, runWords, runsOf, type SeamParts } from "./seam-edge.ts";

/**
 * The seam continuity check (ADR-109 decision 6, P1.6-T152, and the note on it): what is painted along each side of
 * an edge that has a placed, painted neighbour, against the crossings of the seam between the two maps. Every
 * result is a warning, ignorable, never a refusal of a save or a compile. Everything is told in the corner numbers
 * of the map in view; the classes of ground are the game's (`DaedalusGameAdapter.seamGround`).
 */


/**
 * The kind of a crossing whose span holds ground of the wrong class.
 */
export const SEAM_CROSSING_CLASS_KIND = "seam.crossing-class";

/**
 * The kind of a crossing painted wider, narrower or elsewhere than its span.
 */
export const SEAM_CROSSING_WIDTH_KIND = "seam.crossing-width";

/**
 * The kind of a path or water that reaches an edge where no crossing is listed.
 */
export const SEAM_CROSSING_UNLISTED_KIND = "seam.crossing-unlisted";

/**
 * The kind of a walkable run across a closed edge.
 */
export const SEAM_CLOSED_KIND = "seam.closed";

/**
 * The kind of a crossing with no position yet.
 */
export const SEAM_CROSSING_PENDING_KIND = "seam.crossing-pending";


type Ground = SeamGround | "open";

type Run = readonly [number, number];

type Which = "here" | "there";

type Word = "path" | "water";

/**
 * One thing found on one side of an edge, before the two sides are merged: the kind, a key that makes two
 * findings the same finding, and how its message is told.
 */
interface Finding {
  readonly kind: string;
  readonly key: string;
  readonly sides: Which[];
  readonly box: Run;
  readonly say: (lead: string) => string;
  readonly move?: { readonly from: number; readonly to: number; readonly crossing: SeamCrossing };
}

/**
 * The index of the corner of a map along one of its sides.
 *
 * @param map The map.
 * @param side The side.
 * @param along The corner's number along the side.
 * @returns The index into a terrain layer's corners.
 */
function cornerIndex(map: MapDocument, side: MapSide, along: number): number {
  const row = map.width + 1;
  switch (side) {
    case "north":
      return along;
    case "south":
      return map.height * row + along;
    case "west":
      return along * row;
    default:
      return along * row + map.width;
  }
}

/**
 * The class of ground at one corner on a side of a map: water over blocked over path over open ground, over every
 * terrain layer that paints the corner.
 *
 * @param map The map.
 * @param side The side.
 * @param along The corner's number along the side.
 * @param groundOf The class of a terrain id, memoised.
 * @returns The class; open ground when no layer paints a class there.
 */
function groundAt(map: MapDocument, side: MapSide, along: number, groundOf: (terrain: string) => SeamGround | undefined): Ground {
  const index = cornerIndex(map, side, along);
  let found: Ground = "open";
  for (const layer of map.layers) {
    if (layer.kind !== "terrain" || (layer.corners[index] ?? 0) === 0) {
      continue;
    }
    const ground = groundOf(layer.terrain);
    if (ground === "water") {
      return "water";
    }
    if (ground === "blocked") {
      found = "blocked";
    } else if (ground === "path" && found === "open") {
      found = "path";
    }
  }
  return found;
}

/**
 * The words for a list of runs: `corners 12 to 13 (2 corners)`, joined with `and`.
 *
 * @param runs The runs.
 * @returns The words.
 */
function runsWords(runs: readonly Run[]): string {
  return runs.map((run) => runWords("corner", run)).join(" and ");
}

/**
 * How far apart two runs lie, 0 when they touch or overlap.
 *
 * @param a One run.
 * @param b The other.
 * @returns The gap in corners.
 */
function gapBetween(a: Run, b: Run): number {
  return Math.max(0, a[0] - b[1], b[0] - a[1]);
}

/**
 * The findings of one side of an edge for one class of crossing.
 *
 * @param which The side.
 * @param word The class's word, `path` or `water`.
 * @param runs The runs of that class on the side.
 * @param listed The seam's crossings of the class with a position, as their spans in the numbering of the map in view.
 * @param grounds The class of each corner of the side, by corner number.
 * @param side The side of the map in view.
 * @param open Whether unlisted runs of the class are reported on this seam.
 * @param seamKey The seam's key, or null when none is recorded.
 * @returns The findings.
 */
function sideFindings(which: Which, word: Word, runs: readonly Run[], listed: readonly { readonly crossing: SeamCrossing; readonly span: Run; readonly seam: Seam }[], grounds: ReadonlyMap<number, Ground>, side: MapSide, open: boolean, seamKey: string | null): Finding[] {
  const findings: Finding[] = [];
  const claimed = new Set<number>();
  const flagged = new Set<string>();
  const unmet: typeof listed[number][] = [];
  for (const entry of listed) {
    const { crossing, span, seam } = entry;
    const key = `${seam.a}/${seam.b}`;
    if (word === "path") {
      const bad: number[] = [];
      for (let corner = span[0]; corner <= span[1]; corner += 1) {
        const ground = grounds.get(corner);
        if (ground === "water" || ground === "blocked") {
          bad.push(corner);
        }
      }
      if (bad.length > 0) {
        flagged.add(crossing.name);
        findings.push({ kind: SEAM_CROSSING_CLASS_KIND, key: `${crossing.name}|${bad.join(",")}`, sides: [which], box: span, say: (lead) => `${lead} water or blocked ground inside the span ${runWords("corner", span)} of crossing ${crossing.name} on seam ${key} along the ${side} edge; a walking crossing needs a path or open ground at every corner of its span` });
      }
    }
    const meeting = runs.map((run, index) => [run, index] as const).filter(([run]) => run[1] >= span[0] && run[0] <= span[1]);
    if (meeting.length === 0) {
      unmet.push(entry);
      continue;
    }
    for (const [, index] of meeting) {
      claimed.add(index);
    }
    const exact = meeting.length === 1 && (meeting[0] as readonly [Run, number])[0][0] === span[0] && (meeting[0] as readonly [Run, number])[0][1] === span[1];
    if (!exact && !flagged.has(crossing.name)) {
      findings.push(widthFinding(which, word, crossing, seam, span, meeting.map(([run]) => run), side));
    }
  }
  for (const entry of unmet) {
    const { crossing, span, seam } = entry;
    let best = -1;
    runs.forEach((run, index) => {
      if (!claimed.has(index) && (best < 0 || gapBetween(run, span) < gapBetween(runs[best] as Run, span))) {
        best = index;
      }
    });
    if (best >= 0) {
      claimed.add(best);
      if (!flagged.has(crossing.name)) {
        findings.push(widthFinding(which, word, crossing, seam, span, [runs[best] as Run], side));
      }
    } else if (word === "water") {
      const key = `${seam.a}/${seam.b}`;
      findings.push({ kind: SEAM_CROSSING_CLASS_KIND, key: `${crossing.name}|none`, sides: [which], box: span, say: (lead) => `${lead} no water inside the span ${runWords("corner", span)} of crossing ${crossing.name} on seam ${key} along the ${side} edge; a water crossing needs water at every corner of its span` });
    }
  }
  if (open) {
    runs.forEach((run, index) => {
      if (!claimed.has(index)) {
        findings.push({ kind: SEAM_CROSSING_UNLISTED_KIND, key: `${word}|${run[0]}-${run[1]}`, sides: [which], box: run, say: (lead) => `${lead} ${word} painted to the ${side} edge at ${runWords("corner", run)} where ${seamKey === null ? "no seam is recorded between the two maps" : `seam ${seamKey} lists no ${word} crossing there`}; list the crossing on the Maps tab or repaint the edge` });
      }
    });
  }
  return findings;
}

/**
 * The finding for a crossing painted wider, narrower or elsewhere than its span.
 *
 * @param which The side.
 * @param word The class's word.
 * @param crossing The crossing.
 * @param seam Its seam.
 * @param span Its span in the numbering of the map in view.
 * @param painted The runs painted where the span is.
 * @param side The side of the map in view.
 * @returns The finding, carrying the offer to move the span when the span is not fixed and one run is painted.
 */
function widthFinding(which: Which, word: Word, crossing: SeamCrossing, seam: Seam, span: Run, painted: readonly Run[], side: MapSide): Finding {
  const key = `${seam.a}/${seam.b}`;
  const first = Math.min(span[0], ...painted.map((run) => run[0]));
  const last = Math.max(span[1], ...painted.map((run) => run[1]));
  const advice = crossing.fixed === true ? "the span is fixed, so paint the edge to match the span" : "move the span to the painted run from the Maps tab, or repaint the edge";
  const shift = span[0] - (crossing.from as number);
  const single = painted.length === 1 ? (painted[0] as Run) : undefined;
  return {
    kind: SEAM_CROSSING_WIDTH_KIND,
    key: `${crossing.name}|${painted.map((run) => `${run[0]}-${run[1]}`).join(",")}`,
    sides: [which],
    box: [first, last],
    say: (lead) => `${lead} the ${word} of crossing ${crossing.name} on seam ${key} painted at ${runsWords(painted)} along the ${side} edge, where the span is ${runWords("corner", span)}; ${advice}`,
    ...(crossing.fixed !== true && single !== undefined ? { move: { from: single[0] - shift, to: single[1] - shift, crossing } } : {}),
  };
}

/**
 * The words that open a message for the sides a finding stands on.
 *
 * @param sides The sides.
 * @param map The map in view.
 * @param other The neighbour.
 * @returns The subject and its verb.
 */
function leadOf(sides: readonly Which[], map: MapDocument, other: MapDocument): string {
  if (sides.length === 2) {
    return `map ${map.id} and ${other.name} both have`;
  }
  return sides[0] === "here" ? `map ${map.id} has` : `${other.name} has`;
}

/**
 * Merges the findings of the two sides: one that stands on both sides alike is one finding.
 *
 * @param findings The findings of both sides.
 * @returns The merged findings, in the order they were first found.
 */
function merged(findings: readonly Finding[]): Finding[] {
  const byKey = new Map<string, Finding>();
  for (const finding of findings) {
    const key = `${finding.kind}|${finding.key}`;
    const held = byKey.get(key);
    byKey.set(key, held === undefined || held.sides.includes(finding.sides[0] as Which) ? (held ?? finding) : { ...held, sides: [...held.sides, ...finding.sides] });
  }
  return [...byKey.values()];
}

/**
 * The runs of corners, within the shared stretch, that hold a class on one side.
 *
 * @param grounds The class of each corner by number.
 * @param from The first corner of the stretch.
 * @param to The last.
 * @param ground The class.
 * @returns The runs.
 */
function groundRuns(grounds: ReadonlyMap<number, Ground>, from: number, to: number, ground: Ground): Run[] {
  const found: number[] = [];
  for (let corner = from; corner <= to; corner += 1) {
    if (grounds.get(corner) === ground) {
      found.push(corner);
    }
  }
  return runsOf(found).map((run) => [run[0], run[1]] as Run);
}

/**
 * The findings of one edge that has a seam, or none: the crossings' spans against both sides, the unlisted runs,
 * and for a closed seam the runs a walker could cross.
 *
 * @param parts What the checks read.
 * @param other The neighbour.
 * @param edge Where it touches.
 * @param seam The seam between the two maps, or undefined.
 * @param groundOf The game's class of a terrain id.
 * @returns The findings, both sides merged.
 */
function edgeFindings(parts: SeamParts, other: MapDocument, edge: Neighbour, seam: Seam | undefined, groundOf: (terrain: string) => SeamGround | undefined): Finding[] {
  const { world, map } = parts;
  const shift = alongOrigin(map, edge.side) - alongOrigin(other, edge.side);
  const opposite = OPPOSITE[edge.side];
  const grounds: Record<Which, Map<number, Ground>> = { here: new Map(), there: new Map() };
  for (let corner = edge.from; corner <= edge.to; corner += 1) {
    grounds.here.set(corner, groundAt(map, edge.side, corner, groundOf));
    grounds.there.set(corner, groundAt(other, opposite, corner + shift, groundOf));
  }
  const state = seam?.state ?? "open";
  const crossings = (seam?.crossings ?? []).map((crossing) => ({ crossing, span: crossingSpanFor(world, seam as Seam, crossing, map.id) }));
  const positioned = crossings.flatMap(({ crossing, span }) => (span === null ? [] : [{ crossing, span: [span.from, span.to] as Run, seam: seam as Seam }]));
  const designing = crossings.filter(({ span }) => span === null).map(({ crossing }) => crossing);
  const findings: Finding[] = [];
  const key = seam === undefined ? null : `${seam.a}/${seam.b}`;
  for (const which of ["here", "there"] as const) {
    for (const word of ["path", "water"] as const) {
      const listed = positioned.filter(({ crossing }) => (crossing.kind === "water") === (word === "water"));
      const barred = state !== "open";
      const pending = designing.some((crossing) => (crossing.kind === "water") === (word === "water"));
      findings.push(...sideFindings(which, word, groundRuns(grounds[which], edge.from, edge.to, word), listed, grounds[which], edge.side, !barred && !pending, key));
    }
  }
  if (state !== "open" && designing.length === 0) {
    /**
     * Whether a walker can stand on a class of ground.
     *
     * @param ground The class.
     * @returns True for open ground and a path.
     */
    const walkable = (ground: Ground | undefined): boolean => ground === "open" || ground === "path";
    /**
     * Whether a corner lies in the span of a listed crossing.
     *
     * @param corner The corner's number.
     * @returns True when a crossing's span holds it.
     */
    const inSpan = (corner: number): boolean => positioned.some(({ span }) => corner >= span[0] && corner <= span[1]);
    const loose: number[] = [];
    for (let corner = edge.from; corner <= edge.to; corner += 1) {
      if (walkable(grounds.here.get(corner)) && walkable(grounds.there.get(corner)) && !inSpan(corner)) {
        loose.push(corner);
      }
    }
    for (const run of runsOf(loose)) {
      if (run[1] > run[0]) {
        findings.push({ kind: SEAM_CLOSED_KIND, key: `${run[0]}-${run[1]}`, sides: ["here", "there"], box: [run[0], run[1]], say: (lead) => `${lead} a walkable run across the ${edge.side} edge at ${runWords("corner", [run[0], run[1]])} on seam ${key ?? "none"}, which is ${state}${state === "closed-but" ? " but for its listed crossings" : ""}; paint a cliff, a dyke or a wall along both sides of the line` });
      }
    }
  }
  return merged(findings);
}

/**
 * The continuity check of the crossings of every seam the map in view names, and of its edges that have none.
 * A blank map in view, a blank neighbour and an edge with no neighbour raise nothing.
 *
 * @param parts What the checks read.
 * @returns The warnings, and an info line for each crossing with no position yet.
 */
export function crossingIssues(parts: SeamParts): readonly ValidationIssue[] {
  const { world, map, tileSize } = parts;
  if (!painted(map)) {
    return [];
  }
  const names = new Map((parts.terrains ?? []).map((terrain) => [terrain.id, terrain.name] as const));
  const hook = parts.adapter.seamGround;
  const memo = new Map<string, SeamGround | undefined>();
  /**
   * The game's class of a terrain id, asked once for each.
   *
   * @param terrain The terrain id.
   * @returns The class, or undefined for open ground.
   */
  const groundOf = (terrain: string): SeamGround | undefined => {
    if (!memo.has(terrain)) {
      memo.set(terrain, hook?.({ id: terrain, name: names.get(terrain) ?? terrain }));
    }
    return memo.get(terrain);
  };
  const issues: ValidationIssue[] = [];
  for (const edge of neighboursOf(world, map.id)) {
    const other = world.maps.find((candidate) => candidate.id === edge.map);
    if (other === undefined || !painted(other)) {
      continue;
    }
    const seam = (world.seams ?? []).find((candidate) => (candidate.a === map.id && candidate.b === other.id) || (candidate.b === map.id && candidate.a === other.id));
    for (const crossing of seam?.crossings ?? []) {
      if (crossing.from === undefined || crossing.to === undefined) {
        issues.push({ mapId: map.id, kind: SEAM_CROSSING_PENDING_KIND, severity: "info", ignorable: true, message: `map ${map.id} has the crossing ${crossing.name} of seam ${seam?.a}/${seam?.b} still to be designed; set its position from the Maps tab once it is painted` });
      }
    }
    if (hook === undefined) {
      continue;
    }
    for (const finding of edgeFindings(parts, other, edge, seam, groundOf)) {
      const issue: ValidationIssue = { mapId: map.id, kind: finding.kind, severity: "warning", ignorable: true, box: edgeBox(map, edge.side, finding.box[0], finding.box[1], tileSize), message: finding.say(leadOf(finding.sides, map, other)) };
      issues.push(finding.move === undefined || seam === undefined ? issue : { ...issue, moveCrossing: { a: seam.a, b: seam.b, name: finding.move.crossing.name, from: finding.move.from, to: finding.move.to } });
    }
  }
  return issues;
}
